import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

const VISIT_KEY = "md_pwa_visit_count";
const DISMISS_KEY = "md_pwa_install_dismissed_at";
const INSTALLED_KEY = "md_pwa_installed";
const REMIND_AFTER_DAYS = 7;       // re-show after 7 days if dismissed
const SHOW_AFTER_VISITS = 2;       // show only on 2nd+ visit

/**
 * Listens for `beforeinstallprompt`, gates the install banner behind a
 * minimum-visits + dismissal cooldown, and pops a soft branded prompt.
 *
 * Hidden when the user already has the app installed (display-mode: standalone).
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Track visits
    try {
      const n = Number(localStorage.getItem(VISIT_KEY) || "0") + 1;
      localStorage.setItem(VISIT_KEY, String(n));
    } catch (e) { console.warn("[PWA] visit count write failed:", e?.message || e); }

    // Already installed?
    if (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone) {
      localStorage.setItem(INSTALLED_KEY, "1");
      return;
    }
    if (localStorage.getItem(INSTALLED_KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);

      const visits = Number(localStorage.getItem(VISIT_KEY) || "0");
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || "0");
      const dismissedRecently = dismissedAt && (Date.now() - dismissedAt) < REMIND_AFTER_DAYS * 86400000;
      if (visits >= SHOW_AFTER_VISITS && !dismissedRecently) {
        // Delay a beat so it doesn't fight the page load
        setTimeout(() => setShow(true), 1500);
      }
    };

    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, "1");
      setShow(false);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(INSTALLED_KEY, "1");
    } else {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferred(null);
    setShow(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  if (!show || !deferred) return null;

  return (
    <div className="md-pwa-prompt" role="dialog" aria-label="Install MindDhara" data-testid="pwa-install-prompt">
      <button
        type="button"
        onClick={dismiss}
        className="md-pwa-prompt-close"
        aria-label="Dismiss install prompt"
        data-testid="pwa-install-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="md-pwa-prompt-icon">
        <img src="/favicon.png" alt="MindDhara" />
      </div>
      <div className="md-pwa-prompt-body">
        <div className="md-pwa-prompt-title">Install MindDhara</div>
        <p className="md-pwa-prompt-text">
          Add to your home screen, open faster, no browser bar, and never lose your therapist or cart.
        </p>
        <button
          type="button"
          onClick={install}
          className="md-pwa-prompt-cta"
          data-testid="pwa-install-accept"
        >
          <DownloadSimple size={14} weight="bold" /> Install app
        </button>
      </div>
    </div>
  );
}
